import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import { UpvoteIndicator } from '@mentimeter/slides-presentation-ui/groups/upvote-indicator';
import { useState } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { calculateAdjustedFontSize } from '../utils/calculateAdjustedFontSize';
import type { OpenEndedResponse } from '../types';
import { ClickableOrReadonlyWrapper } from '../ui/ClickableOrReadonlyWrapper';

const ICON_SPACING_RESULTS = 32;

interface ResponseWithUpvotesProps extends Omit<BoxT, 'onClick'> {
  responseText: string;
  participantDisplayName: OpenEndedResponse['participantDisplayName'];
  upvotes: number;
  index: number;
  isTopVoted: boolean;
  onClick: (index: number) => void;
  controls?: React.ReactNode;
  controlsOnHover?: React.ReactNode;
}

export const ResponseWithUpvotes = ({
  responseText,
  participantDisplayName,
  upvotes,
  index,
  onClick,
  isTopVoted,
  controls,
  controlsOnHover,
  ...rest
}: ResponseWithUpvotesProps) => {
  const [shouldShowControlsOnHover, setShouldShowControlsOnHover] =
    useState(false);

  const { useConfig, useTranslate } = usePresentationContext();
  const { readonly } = useConfig();
  const translate = useTranslate();

  return (
    <Box
      width="100%"
      onMouseEnter={() => setShouldShowControlsOnHover(true)}
      onMouseLeave={() => setShouldShowControlsOnHover(false)}
      {...rest}
    >
      <ClickableOrReadonlyWrapper
        onClick={readonly ? null : () => onClick(index)}
        data-testid={`grid-item-${index}`}
        p={4}
        width="100%"
        minHeight="320px"
        // animation will make text fold, this helps a bit
        minWidth="fit-content"
        height="100%"
        bg={shouldShowControlsOnHover ? 'brandWeak' : 'brandWeakest'}
        borderRadius="3xl"
      >
        <Text
          textAlign="left"
          fontSize={calculateAdjustedFontSize(responseText)}
          color="text"
          mr={ICON_SPACING_RESULTS}
          style={{ wordBreak: 'break-word' }}
        >
          {responseText}
        </Text>
        {participantDisplayName && (
          <Text fontSize="125" mt="space3" color="textWeak">
            {participantDisplayName}
          </Text>
        )}
        <UpvoteIndicator
          mt="auto"
          upvotes={upvotes}
          isTopVoted={isTopVoted}
          translate={translate}
        />
      </ClickableOrReadonlyWrapper>

      {controls && !readonly && (
        <Box
          position="absolute"
          height={`calc(100% - ${ICON_SPACING_RESULTS * 2}px)`}
          justifyContent="space-between"
          style={{
            top: `${ICON_SPACING_RESULTS}px`,
            right: `${ICON_SPACING_RESULTS}px`,
          }}
        >
          {controls}
        </Box>
      )}
      {controlsOnHover && !readonly && (
        <Box
          position="absolute"
          height={`calc(100% - ${ICON_SPACING_RESULTS * 2}px)`}
          justifyContent="space-between"
          style={{
            top: `${ICON_SPACING_RESULTS}px`,
            right: `${ICON_SPACING_RESULTS}px`,
            opacity: shouldShowControlsOnHover ? 1 : 0,
            transition: 'opacity 0.5s ease',
          }}
        >
          {controlsOnHover}
        </Box>
      )}
    </Box>
  );
};
